import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Database, MessageSquare, Send, Upload } from "lucide-react";
import * as XLSX from "sheetjs-style";
import ReactMarkdown from "react-markdown";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useToolsApi from "../../hooks/useToolsApi";

const VISUAL_OPTIONS = [
  { key: "bar", label: "Bar Chart" },
  { key: "area", label: "Area Chart" },
  { key: "line", label: "Line Chart" },
  { key: "table", label: "Table" },
  { key: "pie", label: "Pie Chart" },
];

const COLOR_KEYWORDS = {
  blue: "#2563eb",
  red: "#dc2626",
  green: "#16a34a",
  orange: "#ea580c",
  purple: "#7c3aed",
  teal: "#0f766e",
  black: "#111827",
  gray: "#475569",
  grey: "#475569",
  yellow: "#ca8a04",
  pink: "#db2777",
};

const WIZARD_STEPS = {
  SOURCE: "source",
  TABLE: "table",
  KPI: "kpi",
  VISUAL: "visual",
  UPLOAD: "upload",
  READY: "ready",
};

const DATA_SOURCES = {
  FILE: "file",
  DATABASE: "database",
};

const MAX_CHAT_ROWS_CONTEXT = 5000;

const hasRowData = (row) => Object.values(row || {}).some((value) => String(value ?? "").trim() !== "");

const sanitizeRows = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .filter((row) => row && typeof row === "object")
    .map((row) => {
      const normalized = {};
      Object.keys(row).forEach((key) => {
        normalized[key] = typeof row[key] === "string" ? row[key].trim() : row[key];
      });
      return normalized;
    })
    .filter(hasRowData);
};

const parseCsv = (text) => {
  const workbook = XLSX.read(String(text || ""), { type: "string" });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) {
    return [];
  }

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { defval: "" });
  return sanitizeRows(rows);
};

const parseJson = (text) => {
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return sanitizeRows(parsed);
    }
    return [];
  } catch {
    return [];
  }
};

const parseWorkbook = async (file) => {
  const extension = (file.name.split(".").pop() || "").toLowerCase();

  if (extension === "csv") {
    const content = await file.text();
    return parseCsv(content);
  }

  if (extension === "json") {
    const content = await file.text();
    return parseJson(content);
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) {
    return [];
  }
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { defval: "" });
  return sanitizeRows(rows);
};

const getNumericColumns = (rows) => {
  if (!rows.length) {
    return [];
  }

  const headers = Object.keys(rows[0]);
  return headers.filter((header) => {
    let numericCount = 0;
    rows.forEach((row) => {
      const value = Number(row[header]);
      if (!Number.isNaN(value) && row[header] !== "") {
        numericCount += 1;
      }
    });
    return numericCount > 0;
  });
};

const formatCompactNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value ?? "");
  }

  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue >= 1_000_000_000) {
    const compact = (numericValue / 1_000_000_000).toFixed(absoluteValue >= 10_000_000_000 ? 0 : 1);
    return `${compact.replace(/\.0$/, "")}B`;
  }

  if (absoluteValue >= 1_000_000) {
    const compact = (numericValue / 1_000_000).toFixed(absoluteValue >= 10_000_000 ? 0 : 1);
    return `${compact.replace(/\.0$/, "")}M`;
  }

  if (absoluteValue >= 1_000) {
    const compact = (numericValue / 1_000).toFixed(absoluteValue >= 10_000 ? 0 : 1);
    return `${compact.replace(/\.0$/, "")}K`;
  }

  return Number.isInteger(numericValue) ? numericValue.toLocaleString() : numericValue.toFixed(2);
};

const findColumnForKpi = (kpiName, numericColumns) => {
  const cleanedKpi = normalizeText(kpiName)
    .replace(/[_-]+/g, " ")
    .replace(/\b(total|average|avg|count|number|sum|rate|ratio|per|of|the|sold)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleanedKpi) {
    return "";
  }

  const exactMatch = numericColumns.find((column) => {
    const col = normalizeText(column).replace(/[_-]+/g, " ");
    return cleanedKpi.includes(col) || col.includes(cleanedKpi);
  });
  if (exactMatch) {
    return exactMatch;
  }

  const kpiTokens = cleanedKpi.split(" ").filter((token) => token.length > 2);
  const scored = numericColumns
    .map((column) => {
      const col = normalizeText(column).replace(/[_-]+/g, " ");
      const score = kpiTokens.reduce((acc, token) => (col.includes(token) ? acc + 1 : acc), 0);
      return { column, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored[0]?.score > 0 ? scored[0].column : "";
};

const toNumericValues = (rows, column) => rows
  .map((row) => Number(row?.[column]))
  .filter((value) => !Number.isNaN(value));

const calculateMetrics = (rows, numericColumns, selectedKpis) => {
  if (!rows.length) {
    return [];
  }

  const kpisToUse = selectedKpis?.length ? selectedKpis.slice(0, 8) : numericColumns.slice(0, 6).map((col) => titleCase(col));

  return kpisToUse.map((kpiName) => {
    const normalizedKpi = normalizeText(kpiName);
    const matchedColumn = findColumnForKpi(kpiName, numericColumns);

    const revenueCol = numericColumns.find((col) => normalizeText(col).includes("revenue"));
    const ordersCol = numericColumns.find((col) => normalizeText(col).includes("order"));

    let total = 0;
    let average = 0;
    let peak = 0;

    if (normalizedKpi.includes("per order") && revenueCol && ordersCol) {
      const revenueTotal = toNumericValues(rows, revenueCol).reduce((sum, value) => sum + value, 0);
      const ordersTotal = toNumericValues(rows, ordersCol).reduce((sum, value) => sum + value, 0);
      const perOrder = ordersTotal > 0 ? revenueTotal / ordersTotal : 0;
      total = perOrder;
      average = perOrder;
      peak = perOrder;
    } else if (normalizedKpi.includes("count") || normalizedKpi.includes("number of") || normalizedKpi.includes("total orders")) {
      const countValue = rows.length;
      total = countValue;
      average = countValue;
      peak = countValue;
    } else if (matchedColumn) {
      const values = toNumericValues(rows, matchedColumn);
      total = values.reduce((sum, value) => sum + value, 0);
      average = values.length ? total / values.length : 0;
      peak = values.length ? Math.max(...values) : 0;

      if (normalizedKpi.includes("average") || normalizedKpi.includes("rate")) {
        total = average;
      }
    }

    return {
      title: kpiName,
      total: total.toFixed(2),
      average: average.toFixed(2),
      peak: peak.toFixed(2),
    };
  });
};

const extractColorFromText = (text) => {
  const lower = String(text || "").toLowerCase();
  const hexMatch = lower.match(/#([0-9a-f]{3}|[0-9a-f]{6})\b/);
  if (hexMatch) {
    return hexMatch[0];
  }

  const foundKeyword = Object.keys(COLOR_KEYWORDS).find((keyword) => lower.includes(keyword));
  if (foundKeyword) {
    return COLOR_KEYWORDS[foundKeyword];
  }
  return "";
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const stripStepPromptPrefix = (value) => String(value || "").replace(/^\s*step\s*prompt\s*:\s*/i, "").trim();
const joinOrDash = (items) => (items && items.length ? items.join(", ") : "-");

const titleCase = (value) => String(value || "")
  .replace(/[_-]+/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const parseKpiSuggestions = (text) => {
  const raw = String(text || "");
  const parts = raw
    .split(/\n|,|;|\||\u2022|-/)
    .map((item) => item.replace(/^\s*\d+\.?\s*/, "").trim())
    .filter(Boolean)
    .filter((item) => item.length >= 3)
    .filter((item) => !item.toLowerCase().startsWith("step"));

  const normalized = [];
  const seen = new Set();
  parts.forEach((item) => {
    const clean = titleCase(item);
    const key = clean.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push(clean);
    }
  });
  return normalized.slice(0, 12);
};

const deriveKpisFromRows = (rows) => {
  const numericColumns = getNumericColumns(rows);
  const kpis = numericColumns.map((column) => `Total ${titleCase(column)}`);
  return kpis.slice(0, 10);
};

const buildDatasetSummaryFallback = (rows) => {
  if (!rows || rows.length === 0) {
    return "Dataset summary is unavailable because no rows were found.";
  }

  const columns = Object.keys(rows[0] || {});
  const numericColumns = getNumericColumns(rows);
  const topColumns = columns.slice(0, 8).join(", ");
  const topNumeric = numericColumns.slice(0, 5);

  const numericStats = topNumeric.map((column) => {
    const values = rows
      .map((row) => Number(row?.[column]))
      .filter((value) => !Number.isNaN(value));
    if (!values.length) {
      return null;
    }
    const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return `${column}: avg ${avg.toFixed(2)}, min ${min.toFixed(2)}, max ${max.toFixed(2)}`;
  }).filter(Boolean);

  const statsText = numericStats.length ? ` Key numeric stats: ${numericStats.join(" | ")}.` : "";
  return `Dataset has ${rows.length} rows and ${columns.length} columns. Primary columns include ${topColumns}.${statsText}`;
};

const extractSuggestedKpis = (text) => {
  return parseKpiSuggestions(text);
};

const extractSuggestedVisualKeys = (text) => {
  const lower = normalizeText(text);
  return VISUAL_OPTIONS
    .filter((visual) => lower.includes(normalizeText(visual.label)) || lower.includes(normalizeText(visual.key)))
    .map((visual) => visual.key);
};

const VISUAL_COMMAND_MAPPINGS = [
    {
      key: "bar",
      patterns: [
        "bar",
        "bar chart", "bar charts", "bar chat", "bar chats", "bar graph", "bar graphs", "bar grapgh", "bar grapghs",
        "barchart", "barcharts", "barchat", "barchats", "bargraph", "bargraphs",
      ],
    },
    {
      key: "area",
      patterns: [
        "area",
        "area chart", "area charts", "area chat", "area chats", "area graph", "area graphs", "area grapgh", "area grapghs",
        "areachart", "areacharts", "areachat", "areachats", "areagraph", "areagraphs",
      ],
    },
    {
      key: "line",
      patterns: [
        "line",
        "line chart", "line charts", "line chat", "line chats", "line graph", "line graphs", "line grapgh", "line grapghs",
        "linechart", "linecharts", "linechat", "linechats", "linegraph", "linegraphs",
        "trend chart", "trend graph", "trendline",
      ],
    },
    {
      key: "pie",
      patterns: [
        "pie",
        "pile",
        "piee",
        "pie chart", "pie charts", "pie chat", "pie chats", "pie graph", "pie graphs",
        "pile chart", "pile charts", "pile chat", "pile chats", "pile graph", "pile graphs",
        "piee chart", "piee charts", "piee chat", "piee chats", "piee graph", "piee graphs",
        "piechart", "piecharts", "piechat", "piechats", "piegraph", "piegraphs",
        "pilechart", "pilecharts", "pilechat", "pilechats", "pilegraph", "pilegraphs",
        "pieechart", "pieecharts", "pieechat", "pieechats", "pieegraph", "pieegraphs",
        "donut chart", "doughnut chart", "donut graph", "doughnut graph",
      ],
    },
    {
      key: "table",
      patterns: ["table chart", "table charts", "table graph", "table graphs", "table", "tables", "grid"],
    },
  ];

const escapeRegex = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const findPatternPosition = (text, pattern) => {
  const source = String(text || "");
  const normalizedPattern = String(pattern || "").trim();
  if (!source || !normalizedPattern) {
    return -1;
  }

  const regexSource = `\\b${escapeRegex(normalizedPattern).replace(/\\\s+/g, "\\\\s+")}\\b`;
  const match = source.match(new RegExp(regexSource, "i"));
  return typeof match?.index === "number" ? match.index : -1;
};

const resolveVisualKeysFromPrompt = (text) => {
  const lower = normalizeText(text);

  const matches = [];
  VISUAL_COMMAND_MAPPINGS.forEach((mapping) => {
    mapping.patterns.forEach((pattern) => {
      const index = findPatternPosition(lower, pattern);
      if (index >= 0) {
        matches.push({ key: mapping.key, index, patternLength: pattern.length });
      }
    });
  });

  matches.sort((a, b) => a.index - b.index || b.patternLength - a.patternLength);

  const ordered = [];
  const seen = new Set();
  matches.forEach((item) => {
    if (!seen.has(item.key)) {
      seen.add(item.key);
      ordered.push(item.key);
    }
  });

  return ordered;
};

const applyVisualCommand = (promptText, setSelectedVisuals) => {
  const prompt = String(promptText || "").toLowerCase();
  const mentionedVisualKeys = resolveVisualKeysFromPrompt(prompt);
  const visualIntent = mentionedVisualKeys.length > 0 || /(chart|charts|chat|chats|graph|graphs|grapgh|grapghs|visual|visuals|plot|plots)/.test(prompt);
  const hasToKeyword = /\bto\b/.test(prompt);
  const hasFromToPattern = /\bfrom\b[\s\S]*\bto\b/.test(prompt);
  const hasInsteadOfPattern = /\binstead of\b/.test(prompt);
  const hasConnectorKeyword = /\b(to|into|with|for)\b/.test(prompt);
  const toSegment = hasToKeyword ? prompt.split(/\bto\b/).slice(1).join(" to ").trim() : "";
  const fromSegment = hasToKeyword ? (prompt.split(/\bto\b/)[0] || "") : "";
  const targetVisualKeysFromTo = toSegment ? resolveVisualKeysFromPrompt(toSegment) : [];
  const sourceVisualKeysFromTo = fromSegment ? resolveVisualKeysFromPrompt(fromSegment) : [];
  const isOnlyVisualCommand = /\b(only|keep only|show only|just)\b/.test(prompt);
  const isAddVisualCommand = /\b(add|include|along with|also show|show|display|use|put|insert|need|want)\b/.test(prompt);
  const isRemoveVisualCommand = /\b(remove|hide|delete|exclude)\b/.test(prompt);
  const hasReplaceKeyword = /\b(change|switch|replace|convert)\b/.test(prompt);
  const isReplaceVisualCommand =
    (hasReplaceKeyword && (hasToKeyword || mentionedVisualKeys.length >= 2 || hasConnectorKeyword || hasInsteadOfPattern))
    || hasFromToPattern
    || hasInsteadOfPattern;

  if (!visualIntent || mentionedVisualKeys.length === 0) {
    return "";
  }

  const visualLabels = mentionedVisualKeys.map((key) => VISUAL_OPTIONS.find((item) => item.key === key)?.label || key);

  if (isReplaceVisualCommand) {
    let sourceVisualKeys = [];
    let targetVisualKeys = [];

    if (hasInsteadOfPattern) {
      const [beforeInstead = "", afterInstead = ""] = prompt.split(/\binstead of\b/i);
      sourceVisualKeys = resolveVisualKeysFromPrompt(afterInstead);
      targetVisualKeys = resolveVisualKeysFromPrompt(beforeInstead);
    } else if (hasFromToPattern) {
      const fromMatch = prompt.match(/\bfrom\b([\s\S]*?)\bto\b([\s\S]*)/i);
      sourceVisualKeys = resolveVisualKeysFromPrompt(fromMatch?.[1] || "");
      targetVisualKeys = resolveVisualKeysFromPrompt(fromMatch?.[2] || "");
    } else if (hasToKeyword) {
      sourceVisualKeys = sourceVisualKeysFromTo;
      targetVisualKeys = targetVisualKeysFromTo;
    } else if (hasConnectorKeyword) {
      const connectorMatch = prompt.match(/([\s\S]*?)\b(?:into|with|for)\b([\s\S]*)/i);
      sourceVisualKeys = resolveVisualKeysFromPrompt(connectorMatch?.[1] || "");
      targetVisualKeys = resolveVisualKeysFromPrompt(connectorMatch?.[2] || "");
    } else {
      sourceVisualKeys = mentionedVisualKeys.length > 0 ? [mentionedVisualKeys[0]] : [];
      targetVisualKeys = mentionedVisualKeys.slice(1);
    }

    targetVisualKeys = targetVisualKeys.filter((key) => !sourceVisualKeys.includes(key));

    if (targetVisualKeys.length === 0) {
      return "";
    }

    setSelectedVisuals((prev) => {
      const base = sourceVisualKeys.length > 0 ? prev.filter((key) => !sourceVisualKeys.includes(key)) : [...prev];
      return [...new Set([...base, ...targetVisualKeys])];
    });

    const sourceLabels = sourceVisualKeys.map((key) => VISUAL_OPTIONS.find((item) => item.key === key)?.label || key);
    const targetLabels = targetVisualKeys.map((key) => VISUAL_OPTIONS.find((item) => item.key === key)?.label || key);

    if (sourceLabels.length > 0) {
      return `Updated visuals: replaced ${joinOrDash(sourceLabels)} with ${joinOrDash(targetLabels)}.`;
    }
    return `Updated visuals: added ${joinOrDash(targetLabels)}.`;
  }

  if (isOnlyVisualCommand) {
    setSelectedVisuals(mentionedVisualKeys);
    return `Visuals set to: ${joinOrDash(visualLabels)}.`;
  }

  if (isRemoveVisualCommand) {
    setSelectedVisuals((prev) => {
      const next = prev.filter((key) => !mentionedVisualKeys.includes(key));
      return next.length ? next : prev;
    });
    return `Removed visuals: ${joinOrDash(visualLabels)}.`;
  }

  if (isAddVisualCommand) {
    setSelectedVisuals((prev) => [...new Set([...prev, ...mentionedVisualKeys])]);
    return `Added visuals: ${joinOrDash(visualLabels)}.`;
  }

  // Default behavior: if user mentions chart names without explicit add/remove verbs, add them.
  setSelectedVisuals((prev) => [...new Set([...prev, ...mentionedVisualKeys])]);
  return `Added visuals: ${joinOrDash(visualLabels)}.`;

};

const AIDashboardWorkbench = () => {
  const {
    analyzeDashboardApi,
    dashboardChatApi,
    dashboardDatabaseTablesApi,
    dashboardDatabaseTableDataApi,
  } = useToolsApi();
  const [selectedKpis, setSelectedKpis] = useState([]);
  const [availableKpis, setAvailableKpis] = useState([]);
  const [selectedVisuals, setSelectedVisuals] = useState(["bar", "line", "table", "pie"]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [analysisSummary, setAnalysisSummary] = useState("");
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isDashboardGenerated, setIsDashboardGenerated] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isChatGenerating, setIsChatGenerating] = useState(false);
  const [wizardActive, setWizardActive] = useState(false);
  const [wizardStep, setWizardStep] = useState(WIZARD_STEPS.SOURCE);
  const [selectedDataSource, setSelectedDataSource] = useState("");
  const [databaseTables, setDatabaseTables] = useState([]);
  const [selectedDatabaseTable, setSelectedDatabaseTable] = useState("");
  const [isDatabaseLoading, setIsDatabaseLoading] = useState(false);
  const [wizardPromptText, setWizardPromptText] = useState("Click Start Guided Dashboard to create the dashboard step-by-step with AI prompts.");
  const [showGuidedFlowCard, setShowGuidedFlowCard] = useState(true);
  const [chatPanelWidth, setChatPanelWidth] = useState(20);
  const [isResizingChatPanel, setIsResizingChatPanel] = useState(false);
  const [kpiOrder, setKpiOrder] = useState([]);
  const [draggingKpiTitle, setDraggingKpiTitle] = useState("");
  const [chartOrder, setChartOrder] = useState([]);
  const [draggingChartKey, setDraggingChartKey] = useState("");
  const [barColor, setBarColor] = useState("#bb1ccc");
  const [lineColor, setLineColor] = useState("#bb1ccc");
  const [textColor, setTextColor] = useState("#0f172a");
  const [showTopFilter, setShowTopFilter] = useState(false);
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [chatInput, setChatInput] = useState("");
  const layoutRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);

  const filteredRows = useMemo(() => {
    if (!showTopFilter || !filterColumn || !filterValue) {
      return rows;
    }
    return rows.filter((row) => String(row?.[filterColumn] ?? "") === filterValue);
  }, [rows, showTopFilter, filterColumn, filterValue]);

  const filterColumns = useMemo(() => {
    if (!rows.length) {
      return [];
    }
    return Object.keys(rows[0]).filter((column) => {
      const values = new Set(rows.map((row) => String(row?.[column] ?? "").trim()).filter(Boolean));
      return values.size > 1;
    });
  }, [rows]);

  const filterValueOptions = useMemo(() => {
    if (!filterColumn || !rows.length) {
      return [];
    }
    return [...new Set(rows.map((row) => String(row?.[filterColumn] ?? "").trim()).filter(Boolean))].slice(0, 100);
  }, [rows, filterColumn]);

  const numericColumns = useMemo(() => getNumericColumns(filteredRows), [filteredRows]);
  const generatedMetrics = useMemo(
    () => calculateMetrics(filteredRows, numericColumns, selectedKpis),
    [filteredRows, numericColumns, selectedKpis],
  );
  const orderedMetrics = useMemo(() => {
    if (!generatedMetrics.length) {
      return [];
    }

    if (!kpiOrder.length) {
      return generatedMetrics;
    }

    const orderMap = new Map(kpiOrder.map((title, index) => [title, index]));
    return [...generatedMetrics].sort((a, b) => {
      const aOrder = orderMap.has(a.title) ? orderMap.get(a.title) : Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.has(b.title) ? orderMap.get(b.title) : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [generatedMetrics, kpiOrder]);
  const orderedVisuals = useMemo(() => {
    if (!selectedVisuals.length) {
      return [];
    }

    if (!chartOrder.length) {
      return selectedVisuals;
    }

    const orderMap = new Map(chartOrder.map((key, index) => [key, index]));
    return [...selectedVisuals].sort((a, b) => {
      const aOrder = orderMap.has(a) ? orderMap.get(a) : Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.has(b) ? orderMap.get(b) : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [chartOrder, selectedVisuals]);
  const visibleChatMessages = useMemo(
    () => chatMessages.filter((message) => !/^\s*step\s*prompt\s*:/i.test(String(message?.text || ""))),
    [chatMessages],
  );
  const chartSeriesData = useMemo(
    () => orderedMetrics.slice(0, 8).map((metric) => ({
      name: metric.title,
      total: Number(metric.total) || 0,
      average: Number(metric.average) || 0,
      peak: Number(metric.peak) || 0,
    })),
    [orderedMetrics],
  );

  const appendAssistantMessage = (text) => {
    setChatMessages((prev) => [...prev, { role: "assistant", text }]);
  };

  const handleKpiDrop = (targetTitle) => {
    if (!draggingKpiTitle || draggingKpiTitle === targetTitle) {
      return;
    }

    setKpiOrder((prev) => {
      const source = prev.length ? [...prev] : generatedMetrics.map((metric) => metric.title);
      const fromIndex = source.indexOf(draggingKpiTitle);
      const toIndex = source.indexOf(targetTitle);
      if (fromIndex < 0 || toIndex < 0) {
        return source;
      }
      const [moved] = source.splice(fromIndex, 1);
      source.splice(toIndex, 0, moved);
      return source;
    });

    setDraggingKpiTitle("");
  };

  const handleChartDrop = (targetChartKey) => {
    if (!draggingChartKey || draggingChartKey === targetChartKey) {
      return;
    }

    setChartOrder((prev) => {
      const source = prev.length ? [...prev] : [...selectedVisuals];
      const fromIndex = source.indexOf(draggingChartKey);
      const toIndex = source.indexOf(targetChartKey);
      if (fromIndex < 0 || toIndex < 0) {
        return source;
      }
      const [moved] = source.splice(fromIndex, 1);
      source.splice(toIndex, 0, moved);
      return source;
    });

    setDraggingChartKey("");
  };

  const applyDashboardUiCommand = (promptText) => {
    const prompt = String(promptText || "").toLowerCase();

    const updates = [];

    const visualCommandResult = applyVisualCommand(promptText, setSelectedVisuals);
    if (visualCommandResult) {
      updates.push(visualCommandResult);
    }

    const needsGeneratedDashboard = (
      prompt.includes("bar color")
      || prompt.includes("line color")
      || prompt.includes("text color")
      || prompt.includes("kpi text")
      || prompt.includes("graph text")
      || prompt.includes("title color")
      || prompt.includes("filter")
    );

    if (!analysisSummary && !aiAnalysis && updates.length === 0 && needsGeneratedDashboard) {
      return "Please complete dashboard generation first, then I can apply style/filter changes.";
    }

    if (prompt.includes("bar color")) {
      const color = extractColorFromText(promptText) || "#2563eb";
      setBarColor(color);
      updates.push(`Bar color updated to ${color}.`);
    }

    if (prompt.includes("line color")) {
      const color = extractColorFromText(promptText) || "#7dd3fc";
      setLineColor(color);
      updates.push(`Line color updated to ${color}.`);
    }

    if (
      prompt.includes("text color")
      || prompt.includes("kpi text")
      || prompt.includes("graph text")
      || prompt.includes("title color")
    ) {
      const color = extractColorFromText(promptText) || "#0f172a";
      setTextColor(color);
      updates.push(`Text color updated to ${color} for KPI/graph labels.`);
    }

    if (prompt.includes("add filter") || prompt.includes("show filter")) {
      setShowTopFilter(true);
      if (!filterColumn && filterColumns.length > 0) {
        setFilterColumn(filterColumns[0]);
      }
      updates.push("Top filter added.");
    }

    // Natural language filter support: e.g. "filter data region North"
    if (prompt.includes("filter")) {
      const columnsFromRows = rows.length > 0 ? Object.keys(rows[0]) : [];
      const normalizedColumns = columnsFromRows.map((column) => ({
        original: column,
        normalized: normalizeText(column),
      }));

      let matchedColumn = "";
      normalizedColumns.forEach((columnInfo) => {
        if (!matchedColumn && prompt.includes(columnInfo.normalized)) {
          matchedColumn = columnInfo.original;
        }
      });

      if (!matchedColumn && filterColumns.length > 0) {
        // If user asked for filter but didn't mention a known column, keep existing/first.
        matchedColumn = filterColumn || filterColumns[0];
      }

      if (matchedColumn) {
        const rowValues = [...new Set(rows.map((row) => String(row?.[matchedColumn] ?? "").trim()).filter(Boolean))];
        let matchedValue = "";

        // Look for explicit values inside the user prompt (case-insensitive).
        rowValues.forEach((value) => {
          if (!matchedValue && prompt.includes(normalizeText(value))) {
            matchedValue = value;
          }
        });

        // Handle phrases like "region North" or "region = North".
        if (!matchedValue) {
          const columnPattern = new RegExp(`${normalizeText(matchedColumn)}\\s*(?:=|is|to)?\\s*([a-zA-Z0-9_\\- ]+)`, "i");
          const match = String(promptText || "").match(columnPattern);
          if (match && match[1]) {
            const candidate = normalizeText(match[1]);
            const closest = rowValues.find((value) => normalizeText(value) === candidate);
            if (closest) {
              matchedValue = closest;
            }
          }
        }

        setShowTopFilter(true);
        setFilterColumn(matchedColumn);

        if (matchedValue) {
          setFilterValue(matchedValue);
          updates.push(`Filter applied: ${matchedColumn} = ${matchedValue}.`);
        } else {
          // Keep filter visible for manual value pick if value could not be detected.
          setFilterValue("");
          updates.push(`Filter added on ${matchedColumn}. Please select a value.`);
        }
      }
    }

    if (prompt.includes("remove filter") || prompt.includes("hide filter")) {
      setShowTopFilter(false);
      setFilterColumn("");
      setFilterValue("");
      updates.push("Top filter removed.");
    }

    return updates.length > 0 ? updates.join(" ") : "";
  };

  const getFallbackWizardPrompt = (step) => {
    if (step === WIZARD_STEPS.SOURCE) {
      return "Step 1: Choose data source. Select Upload File or Connect Database, then click Next Step.";
    }
    if (step === WIZARD_STEPS.UPLOAD) {
      return "Step 2: Data Upload. Upload your dataset file (CSV/XLS/XLSX/JSON), then click Next Step.";
    }
    if (step === WIZARD_STEPS.TABLE) {
      return "Step 2: Select Table. Connect to database, choose a table, then click Next Step.";
    }
    if (step === WIZARD_STEPS.KPI) {
      return "Step 3: KPI Suggestions. I analyzed your dataset and suggested KPIs. Review and adjust, then click Next Step.";
    }
    if (step === WIZARD_STEPS.VISUAL) {
      return "Step 4: Chart Suggestions. I suggested chart types for your KPIs. Review and adjust, then click Next Step.";
    }
    return "Step 5: All inputs collected. Click Generate Dashboard to run LLM analysis.";
  };

  const getWizardStepLabel = () => {
    if (wizardStep === WIZARD_STEPS.SOURCE) {
      return "Choose Data Source";
    }
    if (wizardStep === WIZARD_STEPS.UPLOAD) {
      return "Upload Dataset";
    }
    if (wizardStep === WIZARD_STEPS.TABLE) {
      return "Select Database Table";
    }
    if (wizardStep === WIZARD_STEPS.KPI) {
      return "KPI Suggestions";
    }
    if (wizardStep === WIZARD_STEPS.VISUAL) {
      return "Chart Suggestions";
    }
    return "Ready to Generate";
  };

  const fetchDatabaseTables = async () => {
    try {
      setIsDatabaseLoading(true);
      const response = await dashboardDatabaseTablesApi();
      const tables = Array.isArray(response?.tables) ? response.tables : [];
      setDatabaseTables(tables);
      if (!selectedDatabaseTable && tables.length > 0) {
        setSelectedDatabaseTable(tables[0]);
      }
      if (tables.length === 0) {
        appendAssistantMessage("Database connected, but no tables were found.");
      }
      return tables;
    } catch (error) {
      const message = error?.response?.data?.error || "Failed to connect to database.";
      appendAssistantMessage(message);
      setDatabaseTables([]);
      setSelectedDatabaseTable("");
      return [];
    } finally {
      setIsDatabaseLoading(false);
    }
  };

  const loadDatabaseTableRows = async (tableName) => {
    if (!tableName) {
      appendAssistantMessage("Please select a database table before continuing.");
      return { ok: false, rowCount: 0 };
    }

    try {
      setIsDatabaseLoading(true);
      const response = await dashboardDatabaseTableDataApi({ tableName, limit: 5000 });
      const tableRows = sanitizeRows(response?.rows || []);

      if (!tableRows.length) {
        appendAssistantMessage(`No data rows were found in table ${tableName}. Please select another table.`);
        return { ok: false, rowCount: 0 };
      }

      setRows(tableRows);
      setUploadedFile({ name: `DB Table: ${tableName}` });
      setAnalysisSummary("");
      setAiAnalysis(null);
      setSelectedKpis([]);
      setAvailableKpis([]);
      setIsDashboardGenerated(false);

      if (showTopFilter) {
        const nextColumns = tableRows.length > 0 ? Object.keys(tableRows[0]) : [];
        const nextColumn = nextColumns.length > 0 ? nextColumns[0] : "";
        setFilterColumn(nextColumn);
        setFilterValue("");
      }

      appendAssistantMessage(`Loaded ${tableRows.length} records from ${tableName}.`);
      return { ok: true, rowCount: tableRows.length };
    } catch (error) {
      const message = error?.response?.data?.error || "Failed to load table data.";
      appendAssistantMessage(message);
      return { ok: false, rowCount: 0 };
    } finally {
      setIsDatabaseLoading(false);
    }
  };

  const requestWizardPrompt = async (step) => {
    const stepMessage =
      step === WIZARD_STEPS.SOURCE
        ? "Ask the user to choose a data source: Upload File or Connect Database. Keep it short."
        : step === WIZARD_STEPS.UPLOAD
          ? "Ask the user to upload a dataset file (CSV/XLS/XLSX/JSON) and click Next Step. Keep it short."
          : step === WIZARD_STEPS.TABLE
            ? "Ask the user to connect to the database, select one table, and click Next Step. Keep it short."
            : step === WIZARD_STEPS.KPI
              ? "Analyze the selected dataset and suggest business KPIs that fit this data. Return only KPI names in a short comma-separated list."
              : step === WIZARD_STEPS.VISUAL
                ? `From this dataset and selected KPIs, suggest visuals only from: ${VISUAL_OPTIONS.map((v) => v.label).join(", ")}. Return short text with suggested chart names.`
                : "Tell user inputs are complete and generation is starting.";

    try {
      setIsChatGenerating(true);
      const response = await dashboardChatApi({
        message: stepMessage,
        context: {
          wizardStep: step,
          dataSource: selectedDataSource,
          selectedDatabaseTable,
          selectedKpis,
          selectedVisuals,
          hasUpload: Boolean(uploadedFile),
          columns: rows.length ? Object.keys(rows[0]) : [],
          rowsSample: rows.slice(0, 120),
        },
      });

      const reply = response?.reply?.trim();
      const cleanedReply = stripStepPromptPrefix(reply || getFallbackWizardPrompt(step));
      setWizardPromptText(cleanedReply);

      if (step === WIZARD_STEPS.KPI) {
        const suggestedKpis = extractSuggestedKpis(cleanedReply);
        const fallbackKpis = deriveKpisFromRows(rows);
        const finalKpis = suggestedKpis.length > 0 ? suggestedKpis : fallbackKpis;
        setAvailableKpis(finalKpis);
        setSelectedKpis(finalKpis.slice(0, Math.min(5, finalKpis.length)));
      }

      if (step === WIZARD_STEPS.VISUAL) {
        const suggestedVisualKeys = extractSuggestedVisualKeys(cleanedReply);
        if (suggestedVisualKeys.length > 0) {
          setSelectedVisuals(suggestedVisualKeys);
        }
      }
    } catch (_error) {
      setWizardPromptText(stripStepPromptPrefix(getFallbackWizardPrompt(step)));
    } finally {
      setIsChatGenerating(false);
    }
  };

  const startGuidedDashboard = async () => {
    setChatMessages((prev) => prev.filter((message) => !String(message?.text || "").toLowerCase().includes("step prompt:")));
    appendAssistantMessage("Guided dashboard started. Step 1: Choose your data source and click Next Step.");
    setSelectedKpis([]);
    setAvailableKpis([]);
    setSelectedDataSource("");
    setDatabaseTables([]);
    setSelectedDatabaseTable("");
    setUploadedFile(null);
    setRows([]);
    setIsDashboardGenerated(false);
    setWizardActive(true);
    setWizardStep(WIZARD_STEPS.SOURCE);
    await requestWizardPrompt(WIZARD_STEPS.SOURCE);
  };

  const handleWizardNextStep = async () => {
    if (!wizardActive) {
      return;
    }

    if (wizardStep === WIZARD_STEPS.SOURCE) {
      if (!selectedDataSource) {
        appendAssistantMessage("Please select a data source before continuing.");
        return;
      }

      if (selectedDataSource === DATA_SOURCES.FILE) {
        setWizardStep(WIZARD_STEPS.UPLOAD);
        await requestWizardPrompt(WIZARD_STEPS.UPLOAD);
        return;
      }

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Connecting to database and loading tables..." },
      ]);
      const tables = await fetchDatabaseTables();
      if (!tables.length) {
        return;
      }
      setWizardStep(WIZARD_STEPS.TABLE);
      await requestWizardPrompt(WIZARD_STEPS.TABLE);
      return;
    }

    if (wizardStep === WIZARD_STEPS.UPLOAD) {
      if (!uploadedFile || rows.length === 0) {
        appendAssistantMessage("Please upload a valid dataset before continuing.");
        return;
      }
      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: `Uploaded Dataset: ${uploadedFile.name} (${rows.length} rows)` },
        { role: "assistant", text: "Dataset uploaded. LLM is suggesting KPIs now." },
      ]);
      setWizardStep(WIZARD_STEPS.KPI);
      await requestWizardPrompt(WIZARD_STEPS.KPI);
      return;
    }

    if (wizardStep === WIZARD_STEPS.TABLE) {
      if (!selectedDatabaseTable) {
        appendAssistantMessage("Please select a database table before continuing.");
        return;
      }

      const loadResult = await loadDatabaseTableRows(selectedDatabaseTable);
      if (!loadResult.ok) {
        return;
      }

      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: `Selected Table: ${selectedDatabaseTable} (${loadResult.rowCount} rows)` },
        { role: "assistant", text: "Table data loaded. LLM is suggesting KPIs now." },
      ]);
      setWizardStep(WIZARD_STEPS.KPI);
      await requestWizardPrompt(WIZARD_STEPS.KPI);
      return;
    }

    if (wizardStep === WIZARD_STEPS.KPI) {
      if (selectedKpis.length === 0) {
        appendAssistantMessage("No KPI suggestions found. Please select at least one KPI to continue.");
        return;
      }
      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: `Selected KPIs: ${joinOrDash(selectedKpis)}` },
        { role: "assistant", text: "KPI selection saved. LLM is suggesting chart types now." },
      ]);
      setWizardStep(WIZARD_STEPS.VISUAL);
      await requestWizardPrompt(WIZARD_STEPS.VISUAL);
      return;
    }

    if (wizardStep === WIZARD_STEPS.VISUAL) {
      if (selectedVisuals.length === 0) {
        appendAssistantMessage("No chart suggestions found. Please select at least one chart to continue.");
        return;
      }
      const visualLabels = selectedVisuals
        .map((key) => VISUAL_OPTIONS.find((item) => item.key === key)?.label || key);
      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: `Selected Visuals: ${joinOrDash(visualLabels)}` },
        { role: "assistant", text: "Chart selection saved. Generating dashboard now." },
      ]);
      setWizardStep(WIZARD_STEPS.READY);
      await requestWizardPrompt(WIZARD_STEPS.READY);
      await handleAnalyze();
    }
  };

  const resetGuidedDashboard = () => {
    setChatMessages((prev) => prev.filter((message) => !String(message?.text || "").toLowerCase().includes("step prompt:")));
    setWizardActive(false);
    setWizardStep(WIZARD_STEPS.SOURCE);
    setSelectedKpis([]);
    setAvailableKpis([]);
    setSelectedDataSource("");
    setDatabaseTables([]);
    setSelectedDatabaseTable("");
    setIsDatabaseLoading(false);
    setIsDashboardGenerated(false);
    setWizardPromptText("Click Start Guided Dashboard to create the dashboard step-by-step with AI prompts.");
    appendAssistantMessage("Guided dashboard flow reset. Click Start Guided Dashboard to begin again.");
  };

  const handleKpiToggle = (kpi) => {
    setSelectedKpis((prev) => {
      if (prev.includes(kpi)) {
        return prev.filter((item) => item !== kpi);
      }
      return [...prev, kpi];
    });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadedFile(file);
    setSelectedDataSource(DATA_SOURCES.FILE);
    setSelectedDatabaseTable("");
    const parsedRows = await parseWorkbook(file);
    setRows(parsedRows);
    setAnalysisSummary("");
    setAiAnalysis(null);
    setSelectedKpis([]);
    setAvailableKpis([]);
    setIsDashboardGenerated(false);
    if (showTopFilter) {
      const nextColumns = parsedRows.length > 0 ? Object.keys(parsedRows[0]) : [];
      const nextColumn = nextColumns.length > 0 ? nextColumns[0] : "";
      setFilterColumn(nextColumn);
      setFilterValue("");
    }
    if (parsedRows.length === 0) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: `No valid data rows were found in ${file.name}. Please upload a file with at least one non-empty data row.`,
        },
      ]);
      return;
    }

    setChatMessages((prev) => [
      ...prev,
      { role: "assistant", text: `Loaded ${parsedRows.length} records from ${file.name}.` },
    ]);
  };

  const handleVisualToggle = (visualKey) => {
    setSelectedVisuals((prev) => {
      if (prev.includes(visualKey)) {
        return prev.filter((item) => item !== visualKey);
      }
      return [...prev, visualKey];
    });
  };

  const handleAnalyze = async () => {
    if (rows.length === 0 || selectedKpis.length === 0 || selectedVisuals.length === 0) {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Please upload a valid file or load a database table, choose at least one KPI, and select at least one visual before analysis.",
        },
      ]);
      return;
    }

    setIsAnalyzing(true);

    try {
      const response = await analyzeDashboardApi({
        kpis: selectedKpis,
        visuals: selectedVisuals,
        rows,
      });

      const llmResult = response?.result || {};
      const llmSummary = String(llmResult.summary || "").trim();
      const summary = llmSummary || buildDatasetSummaryFallback(rows);
      setAiAnalysis(llmResult);
      setAnalysisSummary(summary);
      setIsDashboardGenerated(true);
      setShowGuidedFlowCard(true);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: `## Dataset Summary\n\n${summary}`,
        },
      ]);
      setWizardActive(false);
    } catch (error) {
      const message = error?.response?.data?.error || "LLM analysis failed.";
      setAnalysisSummary(message);
      setIsDashboardGenerated(false);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", text: message },
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSendMessage = async () => {
    const prompt = chatInput.trim();
    if (!prompt || isChatGenerating) {
      return;
    }

    setChatMessages((prev) => [...prev, { role: "user", text: prompt }]);
    setChatInput("");

    const uiActionResult = applyDashboardUiCommand(prompt);
    if (uiActionResult) {
      appendAssistantMessage(uiActionResult);
      return;
    }

    setIsChatGenerating(true);

    try {
      const response = await dashboardChatApi({
        message: prompt,
        context: {
          kpis: selectedKpis,
          visuals: selectedVisuals,
          summary: analysisSummary,
          rowsCount: rows.length,
          columns: rows.length > 0 ? Object.keys(rows[0]) : [],
          filter: {
            enabled: showTopFilter,
            column: filterColumn,
            value: filterValue,
          },
          rowsSample: filteredRows.slice(0, MAX_CHAT_ROWS_CONTEXT),
        },
      });
      const reply = response?.reply || "I could not generate a response.";

      const llmVisualCommandResult = applyVisualCommand(`${prompt} ${reply}`, setSelectedVisuals);
      if (llmVisualCommandResult) {
        setChatMessages((prev) => [...prev, { role: "assistant", text: llmVisualCommandResult }]);
        return;
      }

      setChatMessages((prev) => [...prev, { role: "assistant", text: reply }]);
    } catch (error) {
      const message = error?.response?.data?.error || "Chat request failed.";
      setChatMessages((prev) => [...prev, { role: "assistant", text: message }]);
    } finally {
      setIsChatGenerating(false);
    }
  };

  useEffect(() => {
    if (!isResizingChatPanel) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const container = layoutRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerPct = (pointerX / rect.width) * 100;
      const nextChatPct = 100 - pointerPct;
      const boundedChatPct = Math.min(38, Math.max(15, nextChatPct));
      setChatPanelWidth(boundedChatPct);
    };

    const handleMouseUp = () => {
      setIsResizingChatPanel(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingChatPanel]);

  const gridTemplateColumns = `minmax(0, 1fr) 8px minmax(320px, ${chatPanelWidth}%)`;

  const renderVisualPanel = (visualKey) => {
    if (visualKey === "bar") {
      return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, minHeight: 260 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: textColor }}>Bar Chart</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartSeriesData} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: textColor }} />
              <YAxis tick={{ fontSize: 11, fill: textColor }} tickFormatter={formatCompactNumber} />
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
              <Bar dataKey="total" name="Total" fill={barColor} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (visualKey === "area") {
      return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, minHeight: 260 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: textColor }}>Area Chart</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartSeriesData} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: textColor }} />
              <YAxis tick={{ fontSize: 11, fill: textColor }} tickFormatter={formatCompactNumber} />
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
              <Area type="monotone" dataKey="total" name="Total" stroke={lineColor} fill={barColor} fillOpacity={0.28} strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (visualKey === "line") {
      return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, minHeight: 260 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: textColor }}>Line Chart</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartSeriesData} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: textColor }} />
              <YAxis tick={{ fontSize: 11, fill: textColor }} tickFormatter={formatCompactNumber} />
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
              <Line type="monotone" dataKey="total" name="Total" stroke={lineColor} strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (visualKey === "pie") {
      const piePalette = [barColor, lineColor, "#38bdf8", "#0ea5e9", "#7dd3fc", "#0284c7", "#0369a1", "#60a5fa"];
      const pieData = chartSeriesData
        .map((item) => ({ name: item.name, value: item.total }))
        .filter((item) => item.value > 0);

      return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, minHeight: 280 }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: textColor }}>Pie Chart</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="75%" label>
                {pieData.map((entry, index) => (
                  <Cell key={`pie-cell-${entry.name}`} fill={piePalette[index % piePalette.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => Number(value).toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (visualKey === "table") {
      return (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, overflowX: "auto" }}>
          <p style={{ margin: "0 0 8px", fontWeight: 600, color: textColor }}>Table</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#bb1ccc", color: "#ffffff" }}>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>Metric</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>Total</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>Average</th>
                <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e2e8f0" }}>Peak</th>
              </tr>
            </thead>
            <tbody>
              {orderedMetrics.slice(0, 8).map((metric) => (
                <tr key={`table-${metric.title}`}>
                  <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", color: textColor }}>{metric.title}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", color: textColor }}>{formatCompactNumber(metric.total)}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", color: textColor }}>{formatCompactNumber(metric.average)}</td>
                  <td style={{ padding: 8, borderBottom: "1px solid #f1f5f9", color: textColor }}>{formatCompactNumber(metric.peak)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!generatedMetrics.length) {
      if (kpiOrder.length) {
        setKpiOrder([]);
      }
      return;
    }

    const metricTitles = generatedMetrics.map((metric) => metric.title);
    setKpiOrder((prev) => {
      const stillValid = prev.filter((title) => metricTitles.includes(title));
      const missing = metricTitles.filter((title) => !stillValid.includes(title));
      const next = [...stillValid, ...missing];
      if (next.length === prev.length && next.every((title, index) => title === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [generatedMetrics, kpiOrder.length]);

  useEffect(() => {
    if (!selectedVisuals.length) {
      if (chartOrder.length) {
        setChartOrder([]);
      }
      return;
    }

    setChartOrder((prev) => {
      const stillValid = prev.filter((key) => selectedVisuals.includes(key));
      const missing = selectedVisuals.filter((key) => !stillValid.includes(key));
      const next = [...stillValid, ...missing];
      if (next.length === prev.length && next.every((key, index) => key === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [chartOrder.length, selectedVisuals]);

  return (
    <div
      ref={layoutRef}
      style={{
        display: "grid",
        gridTemplateColumns,
        gap: 8,
        minHeight: "calc(100vh - 150px)",
        height: "auto",
        maxHeight: "none",
        overflow: "visible",
      }}
      className="ai-dashboard-layout"
    >
      <section
        className="ai-dashboard-panel"
        style={{
          background: "linear-gradient(160deg, #f8fbff 0%, #ffffff 100%)",
          border: "1px solid #e5edf8",
          borderRadius: 12,
          padding: 20,
          boxShadow: "0 8px 22px rgba(17, 24, 39, 0.05)",
          alignSelf: "start",
          position: "sticky",
          top: 0,
          height: "calc(100vh - 150px)",
          maxHeight: "calc(100vh - 150px)",
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
        }}
      >
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 24 }}>AI Dashboard Builder</h2>
        <p style={{ color: "#475569", marginTop: 8, marginBottom: 20 }}>
          Use the AI chat panel to guide setup and generate your dashboard.
        </p>

        <div style={{ marginBottom: 14, fontSize: 12, color: "#64748b" }}>
          Drag KPI cards to reorder. Drag chart cards to reposition. Resize chart cards from the bottom-right corner.
        </div>

        {analysisSummary ? (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 10,
              padding: 12,
              marginBottom: 18,
              color: "#1e40af",
              fontSize: 14,
            }}
          >
            {analysisSummary}
          </div>
        ) : null}

        {showTopFilter ? (
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #dbe6f4",
              borderRadius: 10,
              padding: 10,
              marginBottom: 14,
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <strong style={{ color: "#334155", fontSize: 13 }}>Top Filter</strong>
            <select
              value={filterColumn}
              onChange={(event) => {
                setFilterColumn(event.target.value);
                setFilterValue("");
              }}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 12,
                background: "#fff",
              }}
            >
              <option value="">Select column</option>
              {filterColumns.map((column) => (
                <option key={`filter-column-${column}`} value={column}>
                  {column}
                </option>
              ))}
            </select>
            <select
              value={filterValue}
              onChange={(event) => setFilterValue(event.target.value)}
              disabled={!filterColumn}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 12,
                background: "#fff",
              }}
            >
              <option value="">All values</option>
              {filterValueOptions.map((value) => (
                <option key={`filter-value-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setFilterValue("")}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 12,
                background: "#fff",
                color: "#334155",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        ) : null}

        {isAnalyzing ? (
          <div
            style={{
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: 10,
              padding: 10,
              marginBottom: 18,
              color: "#334155",
              fontSize: 13,
            }}
          >
            Generating dashboard insights with LLM...
          </div>
        ) : null}

        <div
          style={{
            background: "#fff",
            border: "1px solid #dbe6f4",
            borderRadius: 10,
            padding: 14,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 12, color: "#1e293b", fontSize: 16 }}>Generated Dashboard</h3>
          {isDashboardGenerated && orderedMetrics.length > 0 ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                {orderedMetrics.map((metric) => (
                  <div
                    key={metric.title}
                    draggable
                    onDragStart={() => setDraggingKpiTitle(metric.title)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleKpiDrop(metric.title)}
                    onDragEnd={() => setDraggingKpiTitle("")}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      padding: 12,
                      cursor: "grab",
                      opacity: draggingKpiTitle && draggingKpiTitle === metric.title ? 0.55 : 1,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 12, color: textColor }}>{metric.title}</p>
                    <p style={{ margin: "8px 0 4px", fontWeight: 700, fontSize: 18, color: textColor }}>
                      Total: {formatCompactNumber(metric.total)}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: textColor }}>Avg: {formatCompactNumber(metric.average)}</p>
                    <p style={{ margin: 0, fontSize: 12, color: textColor }}>Peak: {formatCompactNumber(metric.peak)}</p>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                }}
                className="dashboard-chart-grid"
              >
                {orderedVisuals.map((visualKey) => (
                  <div
                    key={`visual-panel-${visualKey}`}
                    draggable
                    onDragStart={() => setDraggingChartKey(visualKey)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleChartDrop(visualKey)}
                    onDragEnd={() => setDraggingChartKey("")}
                    style={{
                      border: "1px solid #dbe6f4",
                      borderRadius: 12,
                      padding: 10,
                      background: "#ffffff",
                      cursor: "grab",
                      minHeight: 220,
                      minWidth: 320,
                      resize: "both",
                      overflow: "auto",
                      opacity: draggingChartKey && draggingChartKey === visualKey ? 0.55 : 1,
                    }}
                  >
                    {renderVisualPanel(visualKey)}
                  </div>
                ))}
              </div>

              {aiAnalysis?.insights?.length ? (
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
                  <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#0f172a" }}>AI Insights</p>
                  <ul style={{ margin: 0, paddingLeft: 18, color: "#334155", fontSize: 13 }}>
                    {aiAnalysis.insights.map((insight, index) => (
                      <li key={`insight-${index}`}>{insight}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <p style={{ margin: 0, color: "#64748b" }}>
              Upload data and run analysis to generate KPI cards and chart recommendations.
            </p>
          )}
        </div>
      </section>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize AI chat panel"
        onMouseDown={() => setIsResizingChatPanel(true)}
        style={{
          width: 8,
          borderRadius: 8,
          background: isResizingChatPanel ? "#60a5fa" : "#cbd5e1",
          cursor: "col-resize",
          userSelect: "none",
        }}
        title="Drag to resize chat panel"
      />

      <aside
        className="ai-chat-panel"
        style={{
          background: "#0f172a",
          borderRadius: 12,
          color: "#e2e8f0",
          display: "flex",
          flexDirection: "column",
          alignSelf: "start",
          position: "sticky",
          top: 0,
          height: "calc(100vh - 150px)",
          maxHeight: "calc(100vh - 150px)",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: 14, borderBottom: "1px solid rgba(148, 163, 184, 0.25)", display: "flex", gap: 8, alignItems: "center" }}>
          <MessageSquare size={16} />
          <strong style={{ fontSize: 14 }}>AI Chat</strong>
        </div>

        <div style={{ padding: 12, overflowY: "auto", flex: 1, minWidth: 0 }}>
          {visibleChatMessages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              style={{
                marginBottom: 10,
                background: message.role === "assistant" ? "rgba(30, 41, 59, 0.9)" : "rgba(37, 99, 235, 0.3)",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: 8,
                padding: 10,
                fontSize: 12,
                lineHeight: 1.5,
                minWidth: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: "#cbd5e1" }}>
                {message.role === "assistant" ? <Bot size={13} /> : null}
                <span>{message.role === "assistant" ? "Assistant" : "You"}</span>
              </div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {message.role === "assistant" ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children, ...props }) => <p style={{ margin: "0 0 6px" }} {...props}>{children}</p>,
                      h2: ({ children, ...props }) => <h2 style={{ margin: "0 0 8px", fontSize: 14, color: "#e2e8f0" }} {...props}>{children}</h2>,
                      ul: ({ children, ...props }) => <ul style={{ margin: "0 0 6px 16px", padding: 0 }} {...props}>{children}</ul>,
                      li: ({ children, ...props }) => <li style={{ marginBottom: 4 }} {...props}>{children}</li>,
                    }}
                  >
                    {String(message.text || "")}
                  </ReactMarkdown>
                ) : (
                  message.text
                )}
              </div>
            </div>
          ))}

          {isChatGenerating && !wizardActive ? (
            <div
              style={{
                marginBottom: 10,
                background: "rgba(30, 41, 59, 0.9)",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: 8,
                padding: 10,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: "#cbd5e1" }}>
                <Bot size={13} />
                <span>Assistant</span>
              </div>
              <div>Generating...</div>
            </div>
          ) : null}

          {showGuidedFlowCard || wizardActive ? (
            <div
              style={{
                marginBottom: 10,
                background: "rgba(30, 41, 59, 0.9)",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: 8,
                padding: 10,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: "#cbd5e1" }}>
                <Bot size={13} />
                <span>Assistant</span>
              </div>

              <div style={{ marginBottom: 8, color: "#e2e8f0" }}>
                {wizardPromptText}
              </div>

              <div
                style={{
                  background: "rgba(8, 47, 73, 0.9)",
                  border: "1px solid rgba(125, 211, 252, 0.4)",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div style={{ marginBottom: 8, color: "#bae6fd", fontWeight: 700 }}>
                  Guided Dashboard Flow
                </div>
                {isChatGenerating || isAnalyzing ? (
                  <div style={{ color: "#e2e8f0", fontSize: 12 }}>Generating guided flow...</div>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      <button
                        type="button"
                        onClick={startGuidedDashboard}
                        disabled={wizardActive || isAnalyzing || isChatGenerating}
                        style={{
                          border: "1px solid #0284c7",
                          borderRadius: 6,
                          padding: "6px 10px",
                          background: wizardActive ? "#7dd3fc" : "#38bdf8",
                          color: "#082f49",
                          fontWeight: 700,
                          cursor: wizardActive || isAnalyzing || isChatGenerating ? "not-allowed" : "pointer",
                        }}
                      >
                        Start Guided Dashboard
                      </button>
                      <button
                        type="button"
                        onClick={resetGuidedDashboard}
                        disabled={isAnalyzing || isChatGenerating}
                        style={{
                          border: "1px solid rgba(148,163,184,0.6)",
                          borderRadius: 6,
                          padding: "6px 10px",
                          background: "#1e293b",
                          color: "#e2e8f0",
                          fontWeight: 700,
                          cursor: isAnalyzing || isChatGenerating ? "not-allowed" : "pointer",
                        }}
                      >
                        Reset
                      </button>
                    </div>

                    {wizardActive ? (
                      <>
                    <div style={{ marginBottom: 8, color: "#e2e8f0" }}>
                      Step: {getWizardStepLabel()}
                    </div>

                    <div
                      style={{
                        marginBottom: 8,
                        border: "1px solid rgba(148,163,184,0.35)",
                        borderRadius: 6,
                        padding: 8,
                        color: "#cbd5e1",
                        fontSize: 11,
                        lineHeight: 1.45,
                      }}
                    >
                      <div>Saved Source: {selectedDataSource === DATA_SOURCES.FILE ? "Upload File" : selectedDataSource === DATA_SOURCES.DATABASE ? "Database" : "-"}</div>
                      <div>Saved Table: {selectedDatabaseTable || "-"}</div>
                      <div>Saved KPIs: {joinOrDash(selectedKpis)}</div>
                      <div>
                        Saved Visuals: {joinOrDash(selectedVisuals.map((key) => VISUAL_OPTIONS.find((item) => item.key === key)?.label || key))}
                      </div>
                      <div>Saved File: {uploadedFile ? `${uploadedFile.name} (${rows.length} rows)` : "-"}</div>
                    </div>

                    {wizardStep === WIZARD_STEPS.SOURCE ? (
                      <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
                        <button
                          type="button"
                          onClick={() => setSelectedDataSource(DATA_SOURCES.FILE)}
                          style={{
                            border: selectedDataSource === DATA_SOURCES.FILE ? "1px solid #38bdf8" : "1px solid rgba(148,163,184,0.6)",
                            background: selectedDataSource === DATA_SOURCES.FILE ? "#0c4a6e" : "#1e293b",
                            color: "#e2e8f0",
                            borderRadius: 6,
                            padding: "7px 9px",
                            fontSize: 11,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <Upload size={12} style={{ marginRight: 6, verticalAlign: "middle" }} /> Upload CSV / XLS / XLSX / JSON
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedDataSource(DATA_SOURCES.DATABASE)}
                          style={{
                            border: selectedDataSource === DATA_SOURCES.DATABASE ? "1px solid #38bdf8" : "1px solid rgba(148,163,184,0.6)",
                            background: selectedDataSource === DATA_SOURCES.DATABASE ? "#0c4a6e" : "#1e293b",
                            color: "#e2e8f0",
                            borderRadius: 6,
                            padding: "7px 9px",
                            fontSize: 11,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <Database size={12} style={{ marginRight: 6, verticalAlign: "middle" }} /> Connect Database
                        </button>
                      </div>
                    ) : null}

                    {wizardStep === WIZARD_STEPS.UPLOAD ? (
                      <div style={{ marginBottom: 8 }}>
                        <label
                          htmlFor="ai-dashboard-upload-chat"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            border: "1px dashed rgba(125,211,252,0.7)",
                            borderRadius: 6,
                            padding: "8px 6px",
                            cursor: "pointer",
                            color: "#e2e8f0",
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        >
                          <Upload size={13} /> Upload CSV / XLS / XLSX / JSON
                        </label>
                        <input
                          id="ai-dashboard-upload-chat"
                          type="file"
                          accept=".csv,.xlsx,.xls,.json"
                          onChange={handleUpload}
                          style={{ display: "none" }}
                        />
                        <p style={{ marginTop: 6, fontSize: 11, color: "#bae6fd" }}>
                          {uploadedFile ? `${uploadedFile.name} loaded` : "No file selected"}
                        </p>
                      </div>
                    ) : null}

                    {wizardStep === WIZARD_STEPS.TABLE ? (
                      <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                        <button
                          type="button"
                          onClick={fetchDatabaseTables}
                          disabled={isDatabaseLoading}
                          style={{
                            border: "1px solid rgba(148,163,184,0.6)",
                            borderRadius: 6,
                            padding: "6px 8px",
                            fontSize: 11,
                            background: "#1e293b",
                            color: "#e2e8f0",
                            cursor: isDatabaseLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {isDatabaseLoading ? "Loading tables..." : "Refresh Table List"}
                        </button>
                        <select
                          value={selectedDatabaseTable}
                          onChange={(event) => setSelectedDatabaseTable(event.target.value)}
                          disabled={isDatabaseLoading || databaseTables.length === 0}
                          style={{
                            border: "1px solid rgba(148,163,184,0.6)",
                            borderRadius: 6,
                            padding: "6px 8px",
                            fontSize: 11,
                            background: "#1e293b",
                            color: "#e2e8f0",
                          }}
                        >
                          <option value="">Select table</option>
                          {databaseTables.map((tableName) => (
                            <option key={`db-table-${tableName}`} value={tableName}>
                              {tableName}
                            </option>
                          ))}
                        </select>
                        <p style={{ margin: 0, fontSize: 11, color: "#bae6fd" }}>
                          {databaseTables.length > 0 ? `${databaseTables.length} tables available` : "No tables loaded yet"}
                        </p>
                      </div>
                    ) : null}

                    {wizardStep === WIZARD_STEPS.KPI ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                        {availableKpis.length === 0 ? (
                          <div style={{ color: "#bae6fd", fontSize: 11 }}>
                            LLM is preparing KPI suggestions from your uploaded dataset.
                          </div>
                        ) : null}
                        {availableKpis.map((kpi) => {
                          const isSelected = selectedKpis.includes(kpi);
                          return (
                            <button
                              key={`chat-kpi-${kpi}`}
                              type="button"
                              onClick={() => handleKpiToggle(kpi)}
                              style={{
                                border: isSelected ? "1px solid #38bdf8" : "1px solid rgba(148,163,184,0.6)",
                                background: isSelected ? "#0c4a6e" : "#1e293b",
                                color: "#e2e8f0",
                                borderRadius: 999,
                                padding: "4px 8px",
                                fontSize: 11,
                                cursor: "pointer",
                              }}
                            >
                              {kpi}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    {wizardStep === WIZARD_STEPS.VISUAL ? (
                      <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                        {VISUAL_OPTIONS.map((visual) => {
                          const isSelected = selectedVisuals.includes(visual.key);
                          return (
                            <button
                              key={`chat-visual-${visual.key}`}
                              type="button"
                              onClick={() => handleVisualToggle(visual.key)}
                              style={{
                                border: isSelected ? "1px solid #38bdf8" : "1px solid rgba(148,163,184,0.6)",
                                background: isSelected ? "#0c4a6e" : "#1e293b",
                                color: "#e2e8f0",
                                borderRadius: 6,
                                padding: "6px 8px",
                                fontSize: 11,
                                textAlign: "left",
                                cursor: "pointer",
                              }}
                            >
                              {visual.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    <button
                      type="button"
                      onClick={handleWizardNextStep}
                      disabled={
                        wizardStep === WIZARD_STEPS.READY
                        || isChatGenerating
                        || isAnalyzing
                        || isDatabaseLoading
                        || (wizardStep === WIZARD_STEPS.SOURCE && !selectedDataSource)
                        || (wizardStep === WIZARD_STEPS.TABLE && !selectedDatabaseTable)
                      }
                      style={{
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 10px",
                        background: (
                          wizardStep === WIZARD_STEPS.READY
                          || (wizardStep === WIZARD_STEPS.SOURCE && !selectedDataSource)
                          || (wizardStep === WIZARD_STEPS.TABLE && !selectedDatabaseTable)
                          || isDatabaseLoading
                        ) ? "#94a3b8" : "#38bdf8",
                        color: "#082f49",
                        fontWeight: 700,
                        cursor: (
                          wizardStep === WIZARD_STEPS.READY
                          || isChatGenerating
                          || isAnalyzing
                          || isDatabaseLoading
                          || (wizardStep === WIZARD_STEPS.SOURCE && !selectedDataSource)
                          || (wizardStep === WIZARD_STEPS.TABLE && !selectedDatabaseTable)
                        ) ? "not-allowed" : "pointer",
                      }}
                    >
                      {wizardStep === WIZARD_STEPS.READY ? "All Steps Completed" : "Next Step"}
                    </button>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ) : null}

        </div>

        <div style={{ padding: 10, borderTop: "1px solid rgba(148, 163, 184, 0.2)", display: "flex", gap: 8 }}>
          <input
            value={chatInput}
            onChange={(event) => setChatInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Ask about this dashboard"
            disabled={isChatGenerating}
            style={{
              flex: 1,
              borderRadius: 8,
              border: "1px solid rgba(148, 163, 184, 0.4)",
              background: "#1e293b",
              color: "#e2e8f0",
              padding: "9px 10px",
              fontSize: 12,
              opacity: isChatGenerating ? 0.7 : 1,
            }}
          />
          <button
            type="button"
            onClick={handleSendMessage}
            disabled={isChatGenerating}
            style={{
              border: "none",
              borderRadius: 8,
              width: 36,
              display: "grid",
              placeItems: "center",
              background: isChatGenerating ? "#93c5fd" : "#2563eb",
              color: "#fff",
              cursor: isChatGenerating ? "not-allowed" : "pointer",
            }}
          >
            <Send size={14} />
          </button>
        </div>
      </aside>

      <style>{`
        @media (max-width: 1500px) {
          .dashboard-chart-grid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 1100px) {
          .ai-dashboard-layout {
            grid-template-columns: 1fr !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }

          .ai-dashboard-panel {
            position: static !important;
            top: auto !important;
            height: auto !important;
            max-height: none !important;
          }

          .ai-chat-panel {
            position: static !important;
            top: auto !important;
            height: auto !important;
            max-height: none !important;
          }

          .ai-dashboard-layout [role="separator"] {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AIDashboardWorkbench;